/* eslint-disable import/no-internal-modules */
import { unlinkAgent } from '#~/agent/components/monitoring/wiring.js';
import { locateProxy } from '#~/agent/utils.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useCalibrate } from '#~/control/components/hooks.js';
import { egress, reboot, sleep, wake } from '#~/device/commands/index.js';
import { useRemove } from '#~/device/components/hooks.js';
import { toDevice } from '#~/device/index.js';
import {
  markTabDismissed,
  releaseConnectionHoldForDevice,
  unregisterMultiAddOrigin
} from '#~/notifications/multi-add-origins.js';
import { post, raiseError } from '#~/notifications/index.js';
import type { Api } from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import { ensureNotNil } from '@studio/js-utils';
import type { Device } from '@studio/models/device';
import type { Settings } from '@studio/models/video';
import type { Configuration as RtspConfig } from '@studio/models/video/rtsp';
import { isRtsp } from '@studio/models/video/rtsp';
import { useLatestFn, useLazyRef, useUpdateEffect, χref } from '@studio/react-utils/hooks';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { Control } from '@tryiris-ai/common.protojs/agent/v1/agent';
import deepmerge from 'deepmerge';
import { dequal } from 'dequal';
import type { RefObject } from 'react';
import { useContext } from 'react';
import { shallow } from 'zustand/shallow';
import { useAgents } from '../../../agent/hooks.js';
import { Context } from './context.js';
import { deriveFooterDismissalTab } from './footer-dismissal.js';
import type { ConnectionVerification } from './connection-verification.js';
import { useDriverTemplate } from './manufacturer/use-driver-template.js';
import { interpretReplaceOutcome } from './replace-outcome.js';
import { useProducer, useStore } from './store/hooks.js';
import { Panel } from './store/index.js';
import type { Wiring } from './types.js';
import { applyVideoSettingsUndoPath } from './undo-state.js';
import { replaceDevice } from './utils.js';

/** @internal */
export const useWiring = (): Wiring => ensureNotNil(useContext(Context));

/** @internal */
export const useWorkflow = (apiRef: RefObject<Api | null>): Wiring => {
  const producer = useProducer();
  const prompt = usePrompt();
  const verification = useStore((state) => state.verification);
  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();

  const [
    agentId,
    candidate,
    nameOverride,
    flags,
    overrides,
    panel,
    lifecycle,
    manufacturer,
    irisConnectionActiveTab
  ] = useStore(
    (µ) => [
      µ.agentId,
      µ.candidate,
      µ.nameOverride,
      µ.flags,
      µ.overrides,
      µ.panel,
      µ.lifecycle,
      µ.manufacturer,
      // Read the current Iris-Connection sub-tab so the post-commit
      // dismissal write knows which
      // `dismissedNeedAttentionTabs[device.id][tab]` to set.
      µ.irisConnectionActiveTab
    ],
    shallow
  );

  const agent = useAgents((µ) => µ.find((a) => a.id === agentId))!;
  const { roomId } = agent ?? {};

  useDriverTemplate();
  // A pending Iris camera has no linked agent from which to resolve its room.
  const settingsRoomId = useStore((µ) => µ.roomId);
  const remove = useRemove({ type: 'video', roomId: settingsRoomId });
  const calibrate = useCalibrate();

  const isValidVideoConfig = (video: Settings | undefined) => {
    if (video === undefined || video.protocol === undefined) {
      return false; // No video settings to validate
    }
    if (isRtsp(video.protocol)) {
      const rtspConfig = video as RtspConfig;
      if (
        rtspConfig.host === undefined ||
        rtspConfig.port === undefined ||
        rtspConfig.host === ''
      ) {
        return false; // RTSP requirese host and port
      }
    }
    // Add more protocol-specific validation as needed
    return true;
  };

  useUpdateEffect(() => {
    const isDirty = !dequal(candidate, lifecycle.snapshot);
    const isVideoValid = isValidVideoConfig(candidate.video);
    const isNameDirty = nameOverride !== undefined && nameOverride !== lifecycle.snapshot?.name;

    producer((draft) => {
      draft.flags.isVideoSettingsDirty = isDirty && isVideoValid;
      draft.flags.isNameDirty = isNameDirty;
      draft.flags.isDirty =
        draft.flags.isDeviceSettingsDirty ||
        draft.flags.isVideoSettingsDirty ||
        draft.flags.isNameDirty;
      if (isDirty) {
        applyVideoSettingsUndoPath(draft, 'subsequent-edit');
      }
    });
  }, [nameOverride, candidate, lifecycle.snapshot]);

  const handleClose = useLatestFn(() => {
    // W5: fire the close half of the advanced-settings funnel (ENG-3960 §D11).
    track(EVENT_NAMES.ADVANCED_SETTINGS_CLOSED, EVENT_CATEGORIES.ADVANCED_SETTINGS, {
      ...(resolveDeviceAnalyticsContext(candidate.id) ?? { device_id: candidate.id }),
      surface: 'video_settings'
    });
    χref(apiRef)?.close(true);
  });

  const handleCommit = useLatestFn(async () => {
    // Is the candidate dirty?
    const retryVerification =
      irisConnectionActiveTab === 'driver' &&
      verification?.step === 'driver' &&
      verification.status !== 'pending' &&
      verification.status !== 'verified';
    if (!flags.isDirty && !retryVerification) {
      return;
    }

    // Submit holds `isBusy` across replaceDevice. Save must not start a second
    // reload, and must raise the flag BEFORE the first `await` so Submit's
    // store hook re-renders (the device-settings mutate below used to run
    // with Save still enabled). `Commands.add({force:true})` bypasses add
    // single-flight, so this flag is the serializer.
    // Per `think-in-systems` (ai.knowledge/global).
    if (flags.isBusy) {
      return;
    }

    // Snapshot the dirty state BEFORE the commit logic clears it. After
    // the save flow runs, `isDeviceSettingsDirty` / `isVideoSettingsDirty`
    // get reset, so we can't read them later. We dispatch the dismissal
    // write eagerly (BEFORE the save below) — the user clicking Save
    // while on a sub-tab with relevant edits IS the acknowledgement
    // signal, regardless of whether the save itself succeeds. Matches
    // the auth-Submit handler in `authentication-content.tsx` which also
    // dismisses regardless of validation outcome.
    //
    // Dirty-flag fallback: the settings store doesn't carry per-tab dirty
    // trackers — only the high-level `isDeviceSettingsDirty` /
    // `isVideoSettingsDirty` / `isNameDirty` flags. We use the active
    // sub-tab + the relevant dirty flag as the gating signal:
    //
    //   - video sub-tab + isVideoSettingsDirty → dismiss 'video'
    //   - control sub-tab + isDeviceSettingsDirty → dismiss 'control'
    //   - driver sub-tab + isDeviceSettingsDirty → dismiss 'driver'
    //
    // The driver and control tabs both produce device-setting edits
    // (brand/model + control protocol both flow through the same
    // `overrides` slice), so we don't distinguish them by flag — only
    // by which tab the user has open at save time. This matches the
    // user's mental model: "I edited the thing on this tab and saved,
    // so this tab is acknowledged."
    //
    // Authentication is dismissed by its OWN local Submit, not by the
    // global Save (see authentication-content.tsx). We deliberately
    // exclude it from this block so a global Save without any auth
    // submit doesn't accidentally dismiss the auth warning.
    //
    // Per `stay-in-scope` (ai.knowledge/global): no new per-tab dirty
    // primitives; reuse the existing flags.
    // Per `reference-rules-in-comments` (ai.knowledge/global).
    const dismissalTab = deriveFooterDismissalTab(irisConnectionActiveTab, {
      isDeviceSettingsDirty: flags.isDeviceSettingsDirty,
      isVideoSettingsDirty: flags.isVideoSettingsDirty
    });
    // Record the dismissal eagerly. The user has explicitly clicked
    // Save while on a sub-tab with edits to that tab's surface — that's
    // the user-action acknowledgement signal.
    // Per `state-sync-optimistic-mutations` (ai.knowledge/platform/studio.client):
    // depot writes are visible on the next render, so the
    // `useNeedAttentionTabs` hook reads the new dismissal slice on the
    // immediate next tick (the proxy.mutate that follows runs async
    // but the depot mutation is already committed locally).
    if (dismissalTab !== undefined) {
      markTabDismissed(candidate.id, dismissalTab);
    }

    // Commit-time advanced-settings telemetry was removed in main's
    // analytics migration: the old `track(EVENT_NAMES.EDIT_ADVANCED_SETTINGS,
    // …)` event (present at the merge-base wiring.ts:146) no longer exists in
    // the event taxonomy — main's domain trackers own the advanced-settings
    // open/section/export events instead, and deliberately dropped the
    // generic "settings saved" event. Resolving the merge to adopt main's
    // deletion here (the footer-dismissal acknowledgement above is the
    // Multi-Add Phase 2 feature we preserve; this telemetry call is not).
    // Per `stay-in-scope` (ai.knowledge/global): no re-introduced event name.

    producer((draft) => {
      draft.flags.isBusy = true;
    });

    try {
      if (
        (panel === Panel.Video && flags.isDeviceSettingsDirty) ||
        flags.isDeviceSettingsDirty ||
        flags.isVideoSettingsDirty
      ) {
        const mutation: DeepPartial<Control> = {
          device: {
            [candidate.id]: {
              deviceSettings: overrides
            }
          }
        };

        const proxy = locateProxy(candidate.id);

        await proxy?.mutate({ control: mutation }, {}, true);

        producer((draft) => {
          draft.overrides = {};
          draft.flags.isDeviceSettingsDirty = false;
          draft.flags.isDirty = draft.flags.isVideoSettingsDirty;
          applyVideoSettingsUndoPath(draft, 'device-only');
        });
      }

      if (flags.isNameDirty) {
        const mutation: DeepPartial<Control> = {
          studio: {
            device: {
              [candidate.id]: {
                name: nameOverride
              }
            }
          }
        };

        const proxy = locateProxy(candidate.id);

        await proxy?.mutate({ control: mutation }, {}, true);

        const token = await getAccessTokenSilently();

        await proxy?.save(token);

        producer((draft) => {
          draft.flags.isNameDirty = false;
          draft.nameOverride = undefined;
        });
      }

      if (!flags.isVideoSettingsDirty && !retryVerification) {
        return;
      }

      const token = await getAccessTokenSilently();

      // Undo is armed only when replaceDevice resolves. A throw must not snapshot
      // a commit that never landed.
      let videoCommitSucceeded = false;
      let committedDevice: Device | undefined;
      const modelChanged = candidate.modelId !== lifecycle.original.modelId;
      const verifyDriver = modelChanged || irisConnectionActiveTab === 'driver';
      let verificationResult: ConnectionVerification | undefined;
      if (verifyDriver)
        producer((draft) => {
          draft.verification = { step: 'driver', status: 'pending' };
        });

      try {
        let orig = lifecycle.original;

        if (candidate.control === undefined && orig.control !== undefined) {
          const { control: _, ...rest } = orig;
          orig = rest;
        }

        if (candidate.video === undefined && orig.video !== undefined) {
          const { video: _, ...rest } = orig;
          orig = rest;
        }

        // ENG-4832: the Driver tab deletes typed_brand / typed_model / identified_by
        // (StudioDevice 13–15) once a listed model replaces the generic one; a key
        // absent from the candidate would otherwise survive the merge below.
        for (const key of ['typedBrand', 'typedModel', 'identifiedBy'] as const) {
          if (candidate[key] === undefined && orig[key] !== undefined) {
            const { [key]: _, ...rest } = orig;
            orig = rest;
          }
        }

        // Replace the device.
        const candidateDeviced = toDevice(candidate);
        const replacement = deepmerge(orig, candidateDeviced);

        // Honor the reload outcome. replaceDevice turns a failed re-add into a
        // RETURNED {ok:false} (it does NOT throw), so the old unconditional
        // `videoCommitSucceeded = true` reported success even when the camera was
        // dropped — a silent camera-loss with no user feedback. Surface the
        // failure and do NOT arm undo-as-success.
        // Per `verify-and-escalate` (ai.knowledge/global).
        const outcome = await replaceDevice(roomId, agentId, replacement, token, {
          verifyConnection: verifyDriver
        });
        if (verifyDriver)
          verificationResult = outcome.ok
            ? (outcome.verification ?? 'unverified')
            : 'connection-failed';
        const decision = interpretReplaceOutcome(outcome, candidate.name);
        videoCommitSucceeded = decision.succeeded;
        committedDevice = replacement as Device;

        if (decision.error) {
          raiseError(decision.error, { title: 'Save failed' });
        }

        if (decision.succeeded) {
          // A promoted replacement ID owns the fresh connection verdict.
          if (decision.deviceId) releaseConnectionHoldForDevice(decision.deviceId);
          // After a successful replaceDevice the preview can show a black image;
          // toggling visibility re-triggers attachTracks to unstick it. Only
          // worth doing when the reload actually bound the device.
          const event = new Event('visibilitychange');
          Object.defineProperty(document, 'visibilityState', { value: 'hidden', writable: true });
          document.dispatchEvent(event);
          // Fire with a timeout to ensure the event is processed.
          setTimeout(() => {
            Object.defineProperty(document, 'visibilityState', {
              value: 'visible',
              writable: true
            });
            document.dispatchEvent(event);
          }, 300);
        }
      } catch (error) {
        // replaceDevice threw (a transport/command error, not a returned
        // {ok:false}). Surface it so the failure is never silent; undo stays
        // unarmed. Per `verify-and-escalate` (ai.knowledge/global).
        videoCommitSucceeded = false;
        raiseError(<Error>error, { title: 'Save failed' });
      }

      // Set flags and update state.
      producer((draft) => {
        applyVideoSettingsUndoPath(
          draft,
          videoCommitSucceeded ? 'video-commit-success' : 'video-commit-failure',
          videoCommitSucceeded ? structuredClone(candidate) : undefined
        );

        if (videoCommitSucceeded) {
          // Auth reloads from lifecycle.original. Advance it to the device
          // that just landed so a later credential submit cannot re-add the
          // pre-save generic model. Unsaved edits after this stay on candidate.
          if (committedDevice) {
            draft.lifecycle.original = structuredClone(committedDevice);
          }
          draft.flags.isDeviceSettingsDirty = false;
          draft.flags.isDirty = false;
          if (verifyDriver) {
            draft.verification = { step: 'driver', status: verificationResult ?? 'unverified' };
            draft.flags.pendingAuthContinuation = verificationResult === 'auth-required';
          }
        }
        // Failure: leave video dirty so Save stays enabled ("Please try again").
      });
    } finally {
      producer((draft) => {
        draft.flags.isBusy = false;
        if (draft.verification?.status === 'pending') draft.verification.status = 'unverified';
      });
    }
  });

  const handleUndo = useLatestFn(async () => {
    if (panel === Panel.Video) {
      producer((draft) => {
        draft.overrides = {};
        draft.flags.isDirty = false;
        draft.flags.canUndo = false;
      });

      return;
    }

    const { rollback } = lifecycle;

    // Can we undo?
    if (!rollback || !flags.canUndo) {
      return;
    }

    if (flags.isBusy) {
      return;
    }

    // Hold busy before the first await so Submit cannot overlap Undo's reload.
    producer((draft) => {
      draft.flags.isBusy = true;
    });

    let restoredDevice: Device | undefined;

    try {
      const token = await getAccessTokenSilently();

      // Rollback the device. Honor the outcome: a failed re-add returns
      // {ok:false} (it does NOT throw), which previously left the user thinking
      // the undo worked while the camera was gone. Surface it.
      // Per `verify-and-escalate` (ai.knowledge/global).
      const replacement = deepmerge(lifecycle.original, toDevice(rollback));
      const outcome = await replaceDevice(roomId, agentId, replacement, token);

      if (!outcome.ok) {
        raiseError(
          `Could not undo the changes to ${rollback.name ?? 'the device'}. Please try again.`,
          { title: 'Undo failed' }
        );
      } else {
        restoredDevice = replacement;
        // Undo re-replaces the device too; clear the resolved id's stale hold so the rolled-back device starts fresh.
        releaseConnectionHoldForDevice(outcome.deviceId);
      }
    } catch (error) {
      // replaceDevice threw — surface it instead of the old silent info log.
      raiseError(<Error>error, { title: 'Undo failed' });
    } finally {
      producer((draft) => {
        draft.flags.isBusy = false;
        if (!restoredDevice) {
          // Keep candidate/snapshot/rollback so the user can retry Undo.
          return;
        }

        const { lifecycle: dl } = draft;

        // Credential Submit reloads from original. Advance it only after Undo
        // lands, using the same device payload that restored the live camera.
        dl.original = structuredClone(restoredDevice);
        draft.candidate = structuredClone(rollback);
        dl.snapshot = rollback;
        dl.rollback = undefined;
        draft.flags.canUndo = false;
      });
    }
  });

  const handleRemove = useLatestFn(() => {
    remove(candidate, (id) => {
      // Keep remediation available when the operator cancels the confirmation.
      unregisterMultiAddOrigin(id.toString());
      handleClose();
    });
  });

  const handleUnlink = useLatestFn(async () => {
    const token = await getAccessTokenSilently();

    unlinkAgent(prompt, agent, token, () => {
      window.location.reload();
    });
  });

  const handleCalibration = useLatestFn(() => {
    if (manufacturer.isOther) {
      // Switch over the the manufacturer tab.
      producer((draft) => {
        draft.panel = Panel.Manufacturer;
      });

      post(
        {
          message:
            "You must first select your camera's brand and model before starting calibration.",
          severity: 'warning',
          title: 'Brand & Model Required',
          deviceId: candidate.id
        },
        { autoClose: true }
      );

      return;
    }

    calibrate(candidate.id);
  });

  const handleReboot = useLatestFn(() => {
    const { id, name } = candidate;

    prompt.confirm({
      message: `Are you sure you want to reboot the source \`${name}\`?`,
      okay: {
        label: 'Reboot Source'
      },
      onConfirm: (answer: boolean) => {
        if (!answer) {
          return;
        }

        reboot(id).then(handleClose).catch(raiseError);
      },
      title: 'Reboot source?'
    });
  });

  const handleSleep = useLatestFn(() => {
    // Track the source sleep event
    track(EVENT_NAMES.SET_SOURCE_TO_SLEEP, EVENT_CATEGORIES.VIDEO_SOURCES, {
      sourceId: candidate.id,
      sourceName: candidate.name,
      sleepMode: true
    });

    sleep(candidate.id).catch(raiseError);
  });

  const handleWake = useLatestFn(() => {
    // Track the source wake event
    track(EVENT_NAMES.WAKE_UP_SOURCE, EVENT_CATEGORIES.VIDEO_SOURCES, {
      sourceId: candidate.id,
      sourceName: candidate.name,
      sleepMode: false
    });

    wake(candidate.id).catch(raiseError);
  });

  // Temporary sends the source name as the sink name, for final implementation,
  // the user will be able to set the sink name or use the source name as default.
  const handleStartEgress = useLatestFn(() => {
    candidate.name;
    egress(candidate.id, candidate.name, true, true).catch(raiseError);
  });

  const handleStopEgress = useLatestFn(() => {
    egress(candidate.id, candidate.name, false).catch(raiseError);
  });

  return useLazyRef(() => ({
    handleCalibration,
    handleCancel: handleClose,
    handleClose,
    handleCommit,
    handleUnlink,
    handleReboot,
    handleRemove,
    handleSleep,
    handleUndo,
    handleWake,
    handleStartEgress,
    handleStopEgress
  })).current;
};
